import React, { useState, useEffect } from 'react';
import { FaSearch, FaUsers, FaBed, FaCalendarAlt, FaDownload, FaTrash, FaEnvelope, FaClock } from 'react-icons/fa';
import { getAllBookingsFromFirestore, updateBookingInFirestore, deleteBookingFromFirestore } from '../utils/firestoreBooking';
import { toast } from 'react-toastify';
import Invoice from './Invoice';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import { sendBookingConfirmationEmail } from '../utils/emailService';

const GuestManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeletingBooking, setIsDeletingBooking] = useState(false);
  const [timeInputModal, setTimeInputModal] = useState({ open: false, action: '', bookingId: '', bookingName: '' });

  // Shared refresh function that syncs with AdminDashboard
  const refreshBookings = async (forceRefresh = false) => {
    // Skip loading if currently deleting a booking
    if (isDeletingBooking) {
      console.log('GuestManagement: Skipping fetch during deletion operation');
      return;
    }
    
    try {
      console.log('GuestManagement: Fetching bookings from Firestore...', forceRefresh ? '(forced)' : '');
      const firestoreBookings = await getAllBookingsFromFirestore(forceRefresh);
      console.log('GuestManagement: Firestore bookings fetched:', firestoreBookings.length);
      
      // Also check localStorage for any backup bookings
      const allLocalBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      console.log('GuestManagement: LocalStorage bookings found:', allLocalBookings.length);
      
      // Merge bookings, avoiding duplicates
      const allBookings = [...firestoreBookings];
      allLocalBookings.forEach(localBooking => {
        const exists = firestoreBookings.some(fb => fb.id === localBooking.id);
        if (!exists) {
          allBookings.push({
            ...localBooking,
            // Ensure consistent data structure
            guestName: localBooking.guestName || localBooking.formData?.firstName + ' ' + localBooking.formData?.lastName || localBooking.name || 'N/A',
            guestEmail: localBooking.guestEmail || localBooking.formData?.email || localBooking.email || 'N/A',
            guestPhone: localBooking.guestPhone || localBooking.formData?.phone || localBooking.phone || 'N/A',
            checkIn: localBooking.checkIn || localBooking.bookingData?.start || 'N/A',
            checkOut: localBooking.checkOut || localBooking.bookingData?.end || 'N/A',
            total: localBooking.total || localBooking.amount || localBooking.bookingData?.total || 0,
            source: 'localStorage' // Mark source for admin visibility
          });
        }
      });
      
      // Sort by creation date (newest first)
      allBookings.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.bookingData?.createdAt || 0);
        const dateB = new Date(b.createdAt || b.bookingData?.createdAt || 0);
        return dateB - dateA;
      });
      
      setBookings(allBookings);
      console.log('GuestManagement: Loaded bookings:', allBookings.length, 'total');
      
      // Trigger custom event for cross-tab sync
      window.dispatchEvent(new CustomEvent('bookingsUpdated', { 
        detail: { bookings: allBookings, timestamp: Date.now() } 
      }));
      
      if (allBookings.length === 0) {
        console.warn('GuestManagement: No bookings found in either Firestore or localStorage');
      }
      
      return allBookings;
    } catch (error) {
      console.error('GuestManagement: Error fetching bookings from Firestore:', error);
      
      // Fallback to localStorage only
      try {
        const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        const processedLocalBookings = localBookings.map(booking => ({
          ...booking,
          // Ensure consistent data structure
          guestName: booking.guestName || booking.formData?.firstName + ' ' + booking.formData?.lastName || booking.name || 'N/A',
          guestEmail: booking.guestEmail || booking.formData?.email || booking.email || 'N/A',
          guestPhone: booking.guestPhone || booking.formData?.phone || booking.phone || 'N/A',
          checkIn: booking.checkIn || booking.bookingData?.start || 'N/A',
          checkOut: booking.checkOut || booking.bookingData?.end || 'N/A',
          total: booking.total || booking.amount || booking.bookingData?.total || 0,
          source: 'localStorage'
        }));
        setBookings(processedLocalBookings);
        console.log('GuestManagement: Loaded from localStorage only:', processedLocalBookings.length);
        
        if (processedLocalBookings.length === 0) {
          console.warn('GuestManagement: No bookings found in localStorage either');
        }
        return processedLocalBookings;
      } catch (localError) {
        console.error('GuestManagement: Error loading from localStorage:', localError);
        setBookings([]);
        return [];
      }
    }
  };

  useEffect(() => {
    refreshBookings();
    
    // Set up polling to refresh bookings every 30 seconds
    const interval = setInterval(() => refreshBookings(), 30000);
    
    // Listen for cross-tab sync events
    const handleBookingsUpdated = (event) => {
      console.log('GuestManagement: Received bookings update event');
      setBookings(event.detail.bookings);
    };
    
    window.addEventListener('bookingsUpdated', handleBookingsUpdated);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('bookingsUpdated', handleBookingsUpdated);
    };
  }, [isDeletingBooking]);

  const handleGuestAction = async (bookingId, action) => {
    const booking = bookings.find(b => b.id === bookingId);
    const guestName = booking?.guestName || booking?.name || 'Guest';
    
    // Open time input modal
    setTimeInputModal({
      open: true,
      action: action,
      bookingId: bookingId,
      bookingName: guestName
    });
  };

  const handleTimeSubmit = async (time) => {
    try {
      const { action, bookingId } = timeInputModal;
      const updateData = {};
      
      if (action === 'check-in') {
        updateData.status = 'checked-in';
        updateData.checkInTime = time;
      } else if (action === 'check-out') {
        updateData.status = 'checked-out';
        updateData.checkOutTime = time;
      }
      
      await updateBookingInFirestore(bookingId, updateData);
      toast.success(`Guest ${action} completed successfully`);
      
      // Refresh bookings and sync across tabs
      await refreshBookings(true);
      
      setTimeInputModal({ open: false, action: '', bookingId: '', bookingName: '' });
    } catch (error) {
      console.error('Error updating booking in Firestore:', error);
      toast.error('Failed to update booking status');
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      // First verify the booking exists in our current data
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) {
        console.error(`Booking ${bookingId} not found in current bookings list`);
        toast.error('Booking not found. Please refresh the page.');
        return;
      }
      
      const updateData = { status: newStatus };
      
      // Add time stamps for check-in/check-out
      if (newStatus === 'checked-in') {
        if (!booking.checkInTime) {
          updateData.checkInTime = new Date().toISOString();
        }
      } else if (newStatus === 'checked-out') {
        if (!booking.checkOutTime) {
          updateData.checkOutTime = new Date().toISOString();
        }
      }
      
      await updateBookingInFirestore(bookingId, updateData);
      
      // Immediately update the local state to reflect the change
      setBookings(prevBookings => 
        prevBookings.map(b => 
          b.id === bookingId 
            ? { ...b, ...updateData }
            : b
        )
      );
      
      // Also refresh from database to ensure sync
      await refreshBookings(true);
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating booking status:', error);
      
      // Handle specific error cases
      if (error.message && error.message.includes('not found in Firestore or localStorage')) {
        toast.error('Booking data not found. Refreshing page...');
        // Force refresh bookings to sync data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error('Failed to update status');
      }
    }
  };
  
  const filteredBookings = bookings.filter(booking => {
    const search = searchTerm.toLowerCase();
    return (
      (booking.id && booking.id.toLowerCase().includes(search)) ||
      (booking.guestName && booking.guestName.toLowerCase().includes(search)) ||
      (booking.name && booking.name.toLowerCase().includes(search)) ||
      (booking.guestEmail && booking.guestEmail.toLowerCase().includes(search)) ||
      (booking.email && booking.email.toLowerCase().includes(search)) ||
      (booking.guestPhone && booking.guestPhone.includes(search)) ||
      (booking.phone && booking.phone.includes(search))
    );
  });
  
  const activeGuests = filteredBookings.filter(b => b.status === 'checked-in');
  const upcomingGuests = filteredBookings.filter(b => b.status === 'confirmed');
  const checkedOutGuests = filteredBookings.filter(b => b.status === 'checked-out');

  // Handle delete booking
  const handleSendConfirmationEmail = async (booking) => {
    try {
      console.log('Sending confirmation email for booking:', booking.id);
      await sendBookingConfirmationEmail({
        guestName: booking.guestName || booking.name,
        guestEmail: booking.guestEmail || booking.email,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        bedType: booking.bedType,
        selectedBeds: booking.selectedBeds || [],
        amount: booking.amount || booking.total,
        bookingId: booking.id,
        extraCharges: booking.extraCharges || []
      });
      toast.success(`Confirmation email sent to ${booking.guestEmail || booking.email}`);
    } catch (error) {
      console.error('Failed to send confirmation email:', error);
      toast.error('Failed to send confirmation email');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    // Set deletion flag to prevent refetching during deletion
    setIsDeletingBooking(true);
    
    console.log('🗑️ Attempting to delete booking with ID:', bookingId);
    console.log('🗑️ Current bookings count:', bookings.length);
    
    let deletionSuccessful = false;
    let localStorageDeleted = false;
    let firestoreDeleted = false;

    // Delete from Firestore first (primary storage)
    try {
      console.log('🗑️ Attempting Firestore deletion...');
      await deleteBookingFromFirestore(bookingId);
      console.log('✅ Deleted from Firestore successfully');
      firestoreDeleted = true;
      deletionSuccessful = true;
    } catch (firestoreError) {
      console.error('❌ Firestore deletion failed:', firestoreError);
      console.error('❌ Firestore error details:', {
        message: firestoreError.message,
        code: firestoreError.code,
        stack: firestoreError.stack
      });
    }

    // Delete from localStorage (backup storage)
    try {
      console.log('🗑️ Attempting localStorage deletion...');
      const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      const initialCount = localBookings.length;
      console.log('🗑️ LocalStorage bookings before deletion:', initialCount);
      
      const updatedLocalBookings = localBookings.filter(booking => booking.id !== bookingId);
      
      if (updatedLocalBookings.length < initialCount) {
        localStorage.setItem('userBookings', JSON.stringify(updatedLocalBookings));
        console.log('✅ Deleted from localStorage successfully');
        console.log('🗑️ LocalStorage bookings after deletion:', updatedLocalBookings.length);
        localStorageDeleted = true;
        deletionSuccessful = true;
      } else {
        console.log('⚠️ Booking not found in localStorage');
      }
    } catch (localError) {
      console.error('❌ Error deleting from localStorage:', localError);
    }

    // Always try to update UI state regardless of storage deletion
    const initialBookingsCount = bookings.length;
    setBookings(prevBookings => {
      const filtered = prevBookings.filter(booking => booking.id !== bookingId);
      console.log('🗑️ UI state bookings before deletion:', prevBookings.length);
      console.log('🗑️ UI state bookings after deletion:', filtered.length);
      return filtered;
    });

    // Show appropriate message and prevent immediate refetch
    if (firestoreDeleted && localStorageDeleted) {
      toast.success('Booking deleted successfully from all storage locations');
    } else if (firestoreDeleted) {
      toast.success('Booking deleted from Firestore (primary storage)');
      
      // Add delay to allow Firestore consistency and reset deletion flag
      console.log('🗑️ Adding delay to prevent immediate refetch...');
      setTimeout(() => {
        console.log('🗑️ Deletion process completed, resetting deletion flag');
        setIsDeletingBooking(false);
      }, 3000);
      
    } else if (localStorageDeleted) {
      toast.success('Booking deleted from local storage');
      setTimeout(() => setIsDeletingBooking(false), 1000);
    } else if (initialBookingsCount > bookings.length - 1) {
      toast.success('Booking removed from display');
      setTimeout(() => setIsDeletingBooking(false), 1000);
    } else {
      toast.error('Booking deletion failed - booking not found');
      console.error('🗑️ Deletion failed for booking ID:', bookingId);
      setIsDeletingBooking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleDateString();
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const handleDownloadInvoice = (booking) => {
    // Compose bookingDetails for Invoice (matching BookingsPage format)
    const bookingDetails = {
      bookingName: booking.formData?.firstName
        ? `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim()
        : (booking.name || booking.guestName || 'Guest'),
      guestName: booking.formData?.firstName
        ? `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim()
        : (booking.name || booking.guestName || 'Guest'),
      bookingId: booking.id || booking.bookingId || booking.bookingData?.id || 'N/A',
      guestDetails: booking.formData ? {
        name: `${booking.formData.firstName} ${booking.formData.lastName || ''}`.trim(),
        email: booking.formData.email,
        phone: booking.formData.phone
      } : {
        name: booking.name || booking.guestName || 'Guest',
        email: booking.guestEmail || booking.userEmail || '',
        phone: booking.guestPhone || ''
      },
      checkInDate: booking.bookingData?.start
        ? new Date(booking.bookingData.start).toLocaleDateString()
        : (booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'N/A'),
      checkOutDate: booking.bookingData?.end
        ? new Date(booking.bookingData.end).toLocaleDateString()
        : (booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'N/A'),
      beds: booking.bookingData?.beds || booking.beds || booking.guests || 1,
      guests: booking.bookingData?.guests || booking.guests || booking.beds || 1,
      nights: booking.bookingData?.nights || booking.nights || (booking.checkIn && booking.checkOut ? Math.ceil((new Date(booking.checkOut)-new Date(booking.checkIn))/(1000*60*60*24)) : 1),
      bedNumbers: (Array.isArray(booking.selectedBeds) && booking.selectedBeds.length > 0)
        ? booking.selectedBeds.map(b => typeof b === 'object' ? (b.number || b.label || b.id || JSON.stringify(b)) : b)
        : (Array.isArray(booking.bedNumbers) && booking.bedNumbers.length > 0
          ? booking.bedNumbers
          : []),
      ratePerNight: booking.bookingData?.pricePerBed || booking.pricePerBed || booking.ratePerNight || 699,
      taxRate: 0, // Set to 0 since convenience fee is already included in booking total
      extraCharges: booking.extraCharges || [],
      checkInTime: booking.checkInTime,
      checkOutTime: booking.checkOutTime,
      packageInfo: booking.packageInfo || booking.bookingData?.package || booking.selectedPackage,
      totalAmount: booking.total || booking.amount || booking.bookingData?.total || booking.totalAmount,
      selectedBeds: booking.selectedBeds || [],
      bookingData: {
        selectedBeds: booking.selectedBeds || []
      }
    };

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${bookingDetails.bookingId}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
          </style>
        </head>
        <body>
          <div id="invoice-root"></div>
        </body>
      </html>
    `);
    setTimeout(() => {
      const rootDiv = printWindow.document.getElementById('invoice-root');
      // Render Invoice to new window
      import('react-dom/client').then(({ createRoot }) => {
        const root = createRoot(rootDiv);
        root.render(React.createElement(Invoice, { bookingDetails }));
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 1000);
      });
    }, 100);
  };
  
  const getStatusBadge = (status) => {
    const statusStyles = {
      'confirmed': { background: '#fef3c7', color: '#92400e' },
      'checked-in': { background: '#dcfce7', color: '#166534' },
      'checked-out': { background: '#e0e7ff', color: '#3730a3' },
      'cancelled': { background: '#fee2e2', color: '#991b1b' },
      'pending': { background: '#ffe4b5', color: '#b8860b' }
    };
    
    return (
      <span style={{
        padding: '0.4rem 0.8rem',
        borderRadius: '20px',
        fontSize: '0.85rem',
        fontWeight: '500',
        textTransform: 'capitalize',
        ...statusStyles[status] || { background: '#f3f4f6', color: '#6b7280' }
      }}>
        {status.replace('-', ' ')}
      </span>
    );
  };
  
  return (
    <div>
      <h3 style={{ color: '#111', marginBottom: '1.5rem', textAlign: 'left' }}>Guest Management</h3>
      
      {/* Search and Stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ position: 'relative', flex: '1', maxWidth: '300px' }}>
          <FaSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
          <input
            type="text"
            placeholder="Search guests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.8rem 0.8rem 0.8rem 2.5rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '1rem' }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ background: '#f8fafc', padding: '0.8rem 1.2rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', minWidth: '120px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0284c7' }}>{activeGuests.length}</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Checked In</div>
          </div>
          <div style={{ background: '#f8fafc', padding: '0.8rem 1.2rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', minWidth: '120px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ea580c' }}>{upcomingGuests.length}</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Upcoming</div>
          </div>
          <div style={{ background: '#f8fafc', padding: '0.8rem 1.2rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', minWidth: '120px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#16a34a' }}>{checkedOutGuests.length}</div>
            <div style={{ fontSize: '0.9rem', color: '#64748b' }}>Checked Out</div>
          </div>
        </div>
      </div>
      
      {/* Guest Table */}
      <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Guest</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Booking ID</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Check-in</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Check-out</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Room/Beds</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Status</th>
              <th style={{ padding: '1.2rem 1rem', textAlign: 'left', fontWeight: '600', color: '#334155', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  No guests found.
                </td>
              </tr>
            ) : (
              filteredBookings
                .filter(b => b.status !== 'cancelled' && b.status !== 'pending')
                .map(booking => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '1rem', fontWeight: '500' }}>
                      <div>{booking.guestName || booking.name || 'N/A'}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>{booking.guestEmail || booking.email || 'N/A'}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>{booking.guestPhone || booking.phone || 'N/A'}</div>
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{booking.id}</td>
                    <td style={{ padding: '1rem' }}>
                      {formatDate(booking.checkIn)}
                      {booking.checkInTime && (
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          <FaClock size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                          {formatTime(booking.checkInTime)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {formatDate(booking.checkOut)}
                      {booking.checkOutTime && (
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          <FaClock size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                          {formatTime(booking.checkOutTime)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div>{booking.bedType || '---'}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.2rem' }}>
                        {booking.selectedBeds && booking.selectedBeds.length > 0 
                          ? booking.selectedBeds.map(bed => bed.display || bed.id).join(', ') 
                          : `${booking.beds || 0} bed${(booking.beds || 0) !== 1 ? 's' : ''}`}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <select 
                          value={booking.status || 'confirmed'}
                          onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                          style={{
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="confirmed">Confirmed</option>
                          <option value="checked-in">Checked In</option>
                          <option value="checked-out">Checked Out</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {booking.checkInTime && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            In: {formatTime(booking.checkInTime)}
                          </div>
                        )}
                        {booking.checkOutTime && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            Out: {formatTime(booking.checkOutTime)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {booking.status === 'confirmed' && (
                          <button 
                            onClick={() => handleGuestAction(booking.id, 'check-in')}
                            title="Check-in Guest"
                            style={{ 
                              background: '#10b981', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '6px', 
                              padding: '0.5rem 1rem', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              fontSize: '0.9rem',
                              marginRight: '0.5rem'
                            }}
                          >
                            <FaUsers size={14} /> Check-in
                          </button>
                        )}
                        {booking.status === 'checked-in' && (
                          <button 
                            onClick={() => handleGuestAction(booking.id, 'check-out')}
                            title="Check-out Guest"
                            style={{ 
                              background: '#3b82f6', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '6px', 
                              padding: '0.5rem 1rem', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              fontSize: '0.9rem',
                              marginRight: '0.5rem'
                            }}
                          >
                            <FaBed size={14} /> Check-out
                          </button>
                        )}
                        <button 
                          onClick={() => handleDownloadInvoice(booking)}
                          title="Download Invoice"
                          style={{ 
                            background: '#8b5cf6', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '0.5rem 1rem', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            fontSize: '0.9rem',
                            marginRight: '0.5rem'
                          }}
                        >
                          <FaDownload size={14} /> Invoice
                        </button>
                        <button 
                          onClick={() => handleSendConfirmationEmail(booking)}
                          title="Send Confirmation Email"
                          style={{ 
                            background: '#f59e0b', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '0.5rem 1rem', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            fontSize: '0.9rem',
                            marginRight: '0.5rem'
                          }}
                        >
                          <FaEnvelope size={14} /> Email
                        </button>
                        <button 
                          onClick={() => handleDeleteBooking(booking.id)}
                          title="Delete Booking"
                          style={{ 
                            background: '#dc3545', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '6px', 
                            padding: '0.5rem 1rem', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            fontSize: '0.9rem'
                          }}
                        >
                          <FaTrash size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Time Input Modal */}
      {timeInputModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '400px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
              {timeInputModal.action === 'check-in' ? 'Check-In' : 'Check-Out'} Time
            </h3>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Guest: <strong>{timeInputModal.bookingName}</strong>
            </p>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const date = formData.get('date');
              const time = formData.get('time');
              const dateTime = new Date(`${date}T${time}`).toISOString();
              handleTimeSubmit(dateTime);
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Date:
                </label>
                <input
                  type="date"
                  name="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Time:
                </label>
                <input
                  type="time"
                  name="time"
                  defaultValue={new Date().toTimeString().slice(0, 5)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setTimeInputModal({ open: false, action: '', bookingId: '', bookingName: '' })}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {timeInputModal.action === 'check-in' ? 'Check In' : 'Check Out'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestManagement;
